import { useState, type FormEvent } from "react";
import { supabase, functionErrorMessage } from "../lib/supabase.js";
import { INDUSTRIES } from "../lib/industries.js";
import { AU_STATES } from "../lib/phone.js";
import { Button, Field, Input, Select } from "../lib/ui.js";

const EMAIL_SHAPE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

function createErrorMessage(message: string): string {
  if (message.includes("client_name_invalid")) return "A client name needs to be between 1 and 100 characters.";
  if (message.includes("business_name_invalid")) return "A business name needs to be between 1 and 100 characters.";
  if (message.includes("billing_email_invalid")) return "That billing email doesn't look like an email address.";
  if (message.includes("industry_invalid")) return "That isn't a valid industry.";
  if (message.includes("state_invalid")) return "That isn't an Australian state or territory.";
  if (message.includes("client_not_found")) return "That client no longer exists.";
  if (message.includes("not_authorized")) return "Only a platform admin can add a client.";
  return message || "That couldn't be created.";
}

interface ClientOption {
  id: string;
  name: string;
}

/**
 * Admin > Clients: add a new client with its first business and invite the owner by email (migration 0081), or add another business
 * to a client that already exists. The state sets the business's time zone; the industry decides which industry rules its receptionist
 * follows. Phone numbers, licences and rates are set up afterwards on their own admin pages.
 */
export function AddClient({ clients, onChanged }: { clients: ClientOption[]; onChanged: () => void }) {
  const [mode, setMode] = useState<"client" | "business">("client");
  const [clientName, setClientName] = useState("");
  const [billingEmail, setBillingEmail] = useState("");
  const [existingClient, setExistingClient] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [industry, setIndustry] = useState("");
  const [state, setState] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setMessage(null);
    const owner = ownerEmail.trim();
    const clientId = existingClient || clients[0]?.id || "";
    if (mode === "client") {
      if (!clientName.trim()) return setError("Enter the client's name.");
      if (!EMAIL_SHAPE.test(billingEmail.trim())) return setError("Enter the billing email.");
      if (!owner) return setError("Enter the owner's email so they can be invited.");
    } else if (!clientId || !businessName.trim()) {
      return setError("Choose the client and enter the business name.");
    }
    if (owner && !EMAIL_SHAPE.test(owner)) return setError("The owner's email doesn't look like an email address.");

    setBusy(true);
    let businessId: string;
    let label: string;
    if (mode === "client") {
      const { data, error: err } = await supabase.rpc("rpc_admin_create_client", {
        p_name: clientName,
        p_billing_email: billingEmail,
        p_business_name: businessName,
        p_industry: industry,
        p_state: state,
      });
      if (err) {
        setBusy(false);
        return setError(createErrorMessage(err.message));
      }
      businessId = (data as { business_id: string }).business_id;
      label = clientName.trim();
    } else {
      const { data, error: err } = await supabase.rpc("rpc_admin_create_business", {
        p_client_id: clientId,
        p_name: businessName,
        p_industry: industry,
        p_state: state,
      });
      if (err) {
        setBusy(false);
        return setError(createErrorMessage(err.message));
      }
      businessId = data as string;
      label = businessName.trim();
    }

    let invited = "";
    if (owner) {
      const { error: inviteErr } = await supabase.functions.invoke("invite-member", { body: { business_id: businessId, email: owner, role: "owner" } });
      if (inviteErr) {
        setError(`${label} was created, but the invitation to ${owner} failed: ${await functionErrorMessage(inviteErr)} Invite them again from the invite section below.`);
      } else {
        invited = ` An invitation was emailed to ${owner}.`;
      }
    }
    setBusy(false);
    if (!owner || invited) setMessage(`Created ${label}.${invited}`);
    setClientName("");
    setBillingEmail("");
    setBusinessName("");
    setOwnerEmail("");
    onChanged();
  }

  return (
    <section style={{ border: "1px solid var(--rc-border)", borderRadius: 10, padding: 16, marginBottom: 20, maxWidth: 640 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
        <h2 style={{ fontSize: 16, margin: 0, flex: 1 }}>Add {mode === "client" ? "a client" : "a business"}</h2>
        <Button
          variant="secondary"
          type="button"
          onClick={() => {
            setMode(mode === "client" ? "business" : "client");
            setError(null);
            setMessage(null);
          }}
        >
          {mode === "client" ? "Add a business to an existing client instead" : "Add a new client instead"}
        </Button>
      </div>
      <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {mode === "client" ? (
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <Field label="Client name">
              <Input value={clientName} maxLength={100} onChange={(e) => setClientName(e.target.value)} style={{ width: 240 }} />
            </Field>
            <Field label="Billing email">
              <Input type="email" value={billingEmail} maxLength={254} onChange={(e) => setBillingEmail(e.target.value)} style={{ width: 260 }} />
            </Field>
          </div>
        ) : (
          <Field label="Client">
            <Select value={existingClient || clients[0]?.id || ""} onChange={(e) => setExistingClient(e.target.value)} style={{ width: 300 }}>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <Field label={mode === "client" ? "Business name (blank = client name)" : "Business name"}>
            <Input value={businessName} maxLength={100} onChange={(e) => setBusinessName(e.target.value)} style={{ width: 260 }} />
          </Field>
          <Field label="Industry">
            <Select value={industry} onChange={(e) => setIndustry(e.target.value)} style={{ width: 180 }}>
              <option value="">Not set</option>
              {INDUSTRIES.map((i) => (
                <option key={i.value} value={i.value}>
                  {i.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="State (sets the time zone)">
            <Select value={state} onChange={(e) => setState(e.target.value)} style={{ width: 220 }}>
              <option value="">Not set</option>
              {AU_STATES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <Field label={mode === "client" ? "Owner's email (they are invited to set a password)" : "Owner's email (optional, to invite a new owner)"}>
          <Input type="email" value={ownerEmail} maxLength={254} onChange={(e) => setOwnerEmail(e.target.value)} style={{ width: 300 }} />
        </Field>
        <div>
          <Button type="submit" variant="primary" disabled={busy}>
            {busy ? "Creating..." : mode === "client" ? "Create client and invite owner" : "Create business"}
          </Button>
        </div>
      </form>
      {error && <p style={{ color: "var(--rc-danger)", fontSize: 12.5, margin: "8px 0 0" }}>{error}</p>}
      {message && <p style={{ color: "var(--rc-success)", fontSize: 12.5, margin: "8px 0 0" }}>{message}</p>}
    </section>
  );
}
